import { Component, inject, output, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { DvDropdownColumn, DvDropdownComponent, DvDropdownDatasourceConfig } from '@devinco/ui/dds';
import { TranslatePipe } from '@ngx-translate/core';
import {
  RouteNetworkStatusData,
  RouteNetworkStatusRenderer,
} from '../route-network-status-renderer/route-network-status.renderer';
import { firstValueFrom, fromEvent, map, takeUntil } from 'rxjs';
import { RouteNetwork } from '@features/map/interfaces/map-data';
import { RouteNetworkFavoritesService } from '@features/map/services/route-network-favorite.service';
import { RestApiService } from '@core/services/rest-api.service';

@Component({
  selector: 'app-route-network-search',
  imports: [DvDropdownComponent, TranslatePipe],
  templateUrl: './route-network-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    :host {
      width: 100%;
    }
    .route-network-search-dropdown {
      width: 100%;
    }
  `,
})
export class RouteNetworkSearchComponent {
  private favoritesService = inject(RouteNetworkFavoritesService);
  private api = inject(RestApiService);

  dropdownColumns: DvDropdownColumn<RouteNetworkStatusData>[] = [
    { field: 'RouteNetworkID', hide: true },
    {
      field: 'LastMaintainedTimeUTC',
      hide: true,
    },
    { field: 'CustomerID', hide: true },
    { field: 'Name', rendererComponent: RouteNetworkStatusRenderer, width: '100%' },
    { field: 'Favorite', hide: true },
    { field: 'Description', hide: true },
    { field: 'Latitude', hide: true },
    { field: 'Longitude', hide: true },
  ];

  routeNetworkSelected = output<RouteNetworkStatusData | undefined>();

  readonly dataSource = {
    chunkSize: 50,
    debounceMs: 100,
    loader: async (params) => {
      const { searchTerm, abortSignal } = params;

      const resolvedFavorites = this.favoritesService.resolvedFavorites() ?? [];

      if (!searchTerm || searchTerm.length < 2) {
        return this.searchComponent()?.items().length
          ? []
          : resolvedFavorites.map((fav) => ({ ...fav, Favorite: true }));
      }

      const abort$ = fromEvent(abortSignal, 'abort');
      const data = await firstValueFrom(
        this.api
          .get<{
            data: RouteNetwork[];
          }>(`Route/Web/SearchNetworks?excludeSegments=true&query=${searchTerm}`, {
            signal: abortSignal,
          })
          .pipe(
            map((resp) => resp.data),
            takeUntil(abort$),
          ),
      );

      const favoriteIds = this.favoritesService.favorites();
      return data.map((item) => ({
        ...item,
        Favorite: favoriteIds.includes(item.RouteNetworkID!),
      }));
    },
  } as DvDropdownDatasourceConfig<any, { entity: string }>;

  protected searchComponent = viewChild<DvDropdownComponent>('search');

  onRouteNetworkSelected(network: RouteNetworkStatusData[]): void {
    this.routeNetworkSelected.emit(network.length ? network[0] : undefined);
  }

  searchPanelToggled(isOpen: boolean): void {
    if (!isOpen) {
      this.searchComponent()?.searchTerm.set('');
      this.searchComponent()?.value.set([]);
    }
  }
}
